/**
 * WCAG 2.2 AA contrast math for tenant branding (D10 guard rendered client-side): the portal
 * re-tints its interactive accents from a tenant `#RRGGBB` primary, and every derived value
 * must hold AA on the surface it lands on. Pure functions - no Angular - so the math is
 * unit-tested beside the service that applies it.
 *
 * The contract is stronger than "pick a text colour": every returned pair is GUARANTEED to
 * clear AA. `onPrimary` is chosen as the higher-contrast of black/white (that maximum is
 * never below 4.58:1 for any sRGB colour, see `onBrandColor`), and the primary's lightness
 * is nudged only in the impossible gap where neither pure ink clears - so the guarantee holds
 * for every input, not just the seeded ones. The `textOnLight`/`textOnDark` shades are
 * verified against the ACTUAL tinted surfaces they render on (the brand tint is a 10% wash of
 * the primary over the canvas), not against pure white/ink.
 */

const HEX_PATTERN = /^#[0-9a-fA-F]{6}$/;

/** WCAG 2.2 AA for normal text; large text and non-text UI need only 3:1. */
const AA_BODY_CONTRAST = 4.5;

const WHITE = '#FFFFFF';
const BLACK = '#000000';

/** Surfaces the AA shades are verified against - mirrors shared/design-tokens.css. */
const LIGHT_SURFACE = WHITE;
const LIGHT_CANVAS = '#F6F7FB';
const DARK_SURFACE = '#141B29';
const DARK_CANVAS = '#0A0E17';

/** The portal scope paints the brand tint as `color-mix(primary 10%, <canvas>)`. */
const BRAND_TINT_SHARE = 0.1;

function channel(value: number): number {
  const c = value / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function luminance(hex: string): number {
  if (!HEX_PATTERN.test(hex)) throw new Error(`Not a #RRGGBB color: ${hex}`);
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

export function isHexColor(value: string | null | undefined): value is string {
  return typeof value === 'string' && HEX_PATTERN.test(value);
}

export function contrastRatio(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  const [lighter, darker] = la >= lb ? [la, lb] : [lb, la];
  return (lighter + 0.05) / (darker + 0.05);
}

/** Mixes `hex` toward black/white by `share` (0-1). */
function mix(hex: string, toward: string, share: number): string {
  const shareClamped = Math.min(Math.max(share, 0), 1);
  const from = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  const to = [1, 3, 5].map((i) => parseInt(toward.slice(i, i + 2), 16));
  return (
    '#' +
    from
      .map((v, i) => Math.round(v + (to[i] - v) * shareClamped))
      .map((v) => v.toString(16).padStart(2, '0'))
      .join('')
  );
}

/** The 10% brand wash the portal paints over `canvas`; text on it must still clear AA. */
export function brandTint(primary: string, canvas: string): string {
  return mix(canvas, primary, BRAND_TINT_SHARE);
}

/**
 * The black-or-white ink that keeps the tenant primary a readable solid fill. Deterministic:
 * the higher of the two contrasts wins, and that maximum is never below 4.58:1 for any sRGB
 * colour, so the returned ink always clears AA against the fill.
 */
export function onBrandColor(primary: string): string {
  return contrastRatio(primary, WHITE) >= contrastRatio(primary, BLACK) ? WHITE : BLACK;
}

/** Nudges `primary`'s lightness away from `ink` until the two clear AA (never needed for the
 * pure black/white inks, but keeps the guarantee explicit and future-proof). */
function nudgeForContrast(primary: string, ink: string): string {
  const away = ink === WHITE ? BLACK : WHITE;
  for (let share = 0; share <= 80; share += 2) {
    const shade = mix(primary, away, share / 100);
    if (contrastRatio(shade, ink) >= AA_BODY_CONTRAST) return shade;
  }
  return primary;
}

/** Darkens toward black until the shade reads as body text on every light ground it lands on
 * (white surface AND the brand's own tint), clearing AA 4.5:1 on the hardest one. */
export function darkenToAA(primary: string): string {
  const grounds = [LIGHT_SURFACE, brandTint(primary, LIGHT_CANVAS)];
  for (let share = 0; share <= 80; share += 2) {
    const shade = mix(primary, BLACK, share / 100);
    if (grounds.every((ground) => contrastRatio(shade, ground) >= AA_BODY_CONTRAST)) return shade;
  }
  return BLACK;
}

/** Lightens toward white until the shade reads as text on the dark surfaces (dark surface AND
 * the dark brand tint), clearing AA 4.5:1 on the hardest one. */
export function lightenToAA(primary: string): string {
  const grounds = [DARK_SURFACE, brandTint(primary, DARK_CANVAS)];
  for (let share = 0; share <= 80; share += 2) {
    const shade = mix(primary, WHITE, share / 100);
    if (grounds.every((ground) => contrastRatio(shade, ground) >= AA_BODY_CONTRAST)) return shade;
  }
  return WHITE;
}

/** Hover moves the fill AWAY from the on-brand ink so the label cannot drop below AA. */
function hoverFor(primary: string, ink: string): string {
  const away = ink === WHITE ? BLACK : WHITE;
  const candidate = mix(primary, away, 0.12);
  return contrastRatio(candidate, ink) >= AA_BODY_CONTRAST ? candidate : primary;
}

/**
 * The one derived set the portal needs (docs/10 section 9: one accent, no decoration):
 * the tenant primary, its AA-safe hover step, and the AA-safe text shades for light and dark
 * surfaces. Any missing or invalid color keeps the caller on rekruto's defaults (the service
 * applies nothing).
 */
export interface TenantPalette {
  readonly primary: string;
  readonly onPrimary: string;
  readonly primaryHover: string;
  readonly textOnLight: string;
  readonly textOnDark: string;
}

export function tenantPalette(primary: string): TenantPalette {
  const ink = onBrandColor(primary);
  const base = contrastRatio(primary, ink) >= AA_BODY_CONTRAST ? primary : nudgeForContrast(primary, ink);
  const normalized = base.toLowerCase();
  return {
    primary: normalized,
    onPrimary: ink,
    primaryHover: hoverFor(normalized, ink),
    textOnLight: darkenToAA(normalized),
    textOnDark: lightenToAA(normalized),
  };
}
